import c from 'classnames';
import { CaretDown } from 'phosphor-icons';
import { FocusEvent, KeyboardEvent } from 'react';

import { Overlay } from '@components/Layout/Overlay';
import {
  SALE_NAV_RED_CLASSES,
  isSaleLink,
  useSaleNavRedTreatment,
} from '../common/saleNav';
import { NavigationItem } from '../types';
import { MegaMenu } from './MegaMenu';

export type DesktopMenuItemProps = {
  item: NavigationItem;
  id: string;
  className?: string;
  isActive: boolean;
  onOpen: () => void;
  onCloseNow: () => void;
};

export const DesktopMenuItem = ({
  item,
  id,
  className = '',
  isActive,
  onOpen,
  onCloseNow,
}: DesktopMenuItemProps) => {
  const doesSubMenuExist = item.columns.length !== 0;
  // label-derived, for test selectors only — `id` is the unique DOM id
  const testId = item.mainLink.value.replace(/\s+/g, '-');
  const isSaleRed = useSaleNavRedTreatment() && isSaleLink(item.mainLink.href);

  const toggleSubNavigation = () => {
    isActive ? onCloseNow() : onOpen();
  };

  // Only the active item may close on blur. When switching items, the newly
  // hidden item's focused link blurs (relatedTarget null); without this guard
  // that stale blur would close the item the pointer just opened.
  const onListItemBlur = (e: FocusEvent) => {
    if (isActive && !e.currentTarget.contains(e.relatedTarget as Node)) {
      onCloseNow();
    }
  };

  const onListItemKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onCloseNow();
    }
  };

  return (
    <li
      data-nav-id={id}
      className={c('first-of-type:-ml-4 last-of-type:-mr-4', className)}
    >
      <div
        role="presentation"
        onKeyDown={onListItemKeyDown}
        onBlur={doesSubMenuExist ? onListItemBlur : undefined}
      >
        <div className="relative flex">
          <a
            className={c(
              'focus-outline block cursor-pointer rounded-sm px-4 py-4 transition',
              {
                'text-blue-dark': isActive && !isSaleRed,
                [SALE_NAV_RED_CLASSES]: isSaleRed,
              }
            )}
            href={item.mainLink.href}
          >
            {item.mainLink.value}
          </a>
          {doesSubMenuExist && (
            <button
              aria-label={item.mainLink.value}
              aria-expanded={isActive}
              aria-controls={id}
              onClick={toggleSubNavigation}
              className="focus-outline pointer-events-none absolute bottom-0 right-0 top-0 rounded-sm fill-white px-0.5 opacity-0 transition focus:pointer-events-auto focus:opacity-100"
            >
              <CaretDown size={8} className={c({ 'rotate-180': isActive })} />
            </button>
          )}
        </div>
        {/* MegaMenu must stay a DOM descendant of the nav <ul>: hovering the
            flyout has to bubble mouseover up to the list's delegated handler so
            the item stays active (the CPS-7574 reachability contract). No portals. */}
        <div
          data-test={`${testId}-menu`}
          className={c({
            hidden: !isActive,
          })}
        >
          {doesSubMenuExist && (
            <>
              <Overlay />
              <MegaMenu id={id} columns={item.columns} />
            </>
          )}
        </div>
      </div>
    </li>
  );
};
